import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ApprovalActionDTO,
  ApprovalDTO,
  ApprovalStatus,
  AuditLogDTO,
  DashboardDTO,
  DatePresetsDTO,
  HealthDTO,
  HolidayDTO,
  MeDTO,
  NotificationDTO,
  Page,
  Permission,
  PresetDTO,
  TaskDetailDTO,
  TaskDTO,
  TaskEventDTO,
  TaskListQuery,
  UserDTO,
  WorkingRules,
} from '@taskos/shared';
import { api, get, qs } from './client';

export const keys = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  tasks: (q: Partial<TaskListQuery>) => ['tasks', q] as const,
  task: (id: string) => ['task', id] as const,
  history: (id: string) => ['task-history', id] as const,
};

export function useMe() {
  // The session probe never triggers the global 401 handler (that would loop forever).
  return useQuery({ queryKey: keys.me, queryFn: ({ signal }) => api<MeDTO>('GET', '/api/me', undefined, { signal, quiet401: true }), retry: false, staleTime: 60_000 });
}

export function useDashboard() {
  return useQuery({ queryKey: keys.dashboard, queryFn: ({ signal }) => get<DashboardDTO>('/api/dashboard', signal), refetchInterval: 60_000 });
}

export function useTasks(q: Partial<TaskListQuery>, enabled = true) {
  return useQuery({
    queryKey: keys.tasks(q),
    queryFn: ({ signal }) => get<Page<TaskDTO>>(`/api/tasks${qs(q as Record<string, unknown>)}`, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({ queryKey: keys.task(id ?? ''), queryFn: ({ signal }) => get<TaskDetailDTO>(`/api/tasks/${id}`, signal), enabled: !!id, retry: (n, e) => n < 2 && (e as { status?: number }).status !== 404 });
}

export function useTaskHistory(id: string | undefined) {
  return useQuery({ queryKey: keys.history(id ?? ''), queryFn: ({ signal }) => get<Page<TaskEventDTO>>(`/api/tasks/${id}/history?limit=100`, signal), enabled: !!id });
}

export function useDatePresets() {
  return useQuery({ queryKey: ['date-presets'], queryFn: ({ signal }) => get<DatePresetsDTO>('/api/dates/presets', signal), staleTime: 60_000 });
}

export interface SnoozeOption {
  id: string;
  label: string;
  until: string | null;
  available: boolean;
}
export function useSnoozeOptions(enabled: boolean) {
  return useQuery({ queryKey: ['snooze-options'], queryFn: ({ signal }) => get<SnoozeOption[]>('/api/snooze-options', signal), enabled, staleTime: 30_000 });
}

export interface ReminderPreset {
  id: string;
  label: string;
  kind: 'BEFORE_DUE' | 'AT_DUE' | 'AFTER_DUE';
  offsetMinutes: number;
}
export function useReminderPresets() {
  return useQuery({
    queryKey: ['reminder-presets'],
    queryFn: ({ signal }) => get<Record<'QUICK' | 'NORMAL' | 'LONG' | 'AT', ReminderPreset[]>>('/api/reminder-presets', signal),
    staleTime: Infinity,
  });
}

export function usePresets(activeOnly = false) {
  return useQuery({ queryKey: ['presets', activeOnly], queryFn: ({ signal }) => get<PresetDTO[]>(`/api/presets${activeOnly ? '?active=true' : ''}`, signal) });
}

export interface DirectoryUser {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
}
export function useDirectory() {
  return useQuery({ queryKey: ['directory'], queryFn: ({ signal }) => get<DirectoryUser[]>('/api/users/directory', signal), staleTime: 60_000 });
}
export function useUsers(enabled: boolean) {
  return useQuery({ queryKey: ['users'], queryFn: ({ signal }) => get<UserDTO[]>('/api/users', signal), enabled });
}
export function useRoles(enabled: boolean) {
  return useQuery({
    queryKey: ['roles'],
    queryFn: ({ signal }) => get<{ roles: Record<string, Permission[]>; permissions: Array<{ id: Permission; label: string }> }>('/api/roles', signal),
    enabled,
  });
}

export interface Taxon {
  id: string;
  name: string;
  color: string | null;
  isArchived: boolean;
}
export function useProjects() {
  return useQuery({ queryKey: ['projects'], queryFn: ({ signal }) => get<Taxon[]>('/api/projects', signal), staleTime: 60_000 });
}
export function useCategories() {
  return useQuery({ queryKey: ['categories'], queryFn: ({ signal }) => get<Taxon[]>('/api/categories', signal), staleTime: 60_000 });
}
export function useTagSuggestions(q: string) {
  return useQuery({ queryKey: ['tags', q], queryFn: ({ signal }) => get<string[]>(`/api/tags${qs({ q })}`, signal), staleTime: 30_000 });
}

export interface SavedView {
  id: string;
  name: string;
  query: Partial<TaskListQuery>;
  viewMode: string;
}
export function useSavedViews() {
  return useQuery({ queryKey: ['views'], queryFn: ({ signal }) => get<SavedView[]>('/api/views', signal) });
}

export function useHolidays(year?: number, search?: string) {
  return useQuery({ queryKey: ['holidays', year, search], queryFn: ({ signal }) => get<HolidayDTO[]>(`/api/holidays${qs({ year, q: search })}`, signal) });
}
export interface DayInfo {
  date: string;
  working: boolean;
  reason: string;
  saturdayOrdinal?: number;
  holidayName?: string;
}
export function useMonthDays(year: number, month: number) {
  return useQuery({ queryKey: ['month', year, month], queryFn: ({ signal }) => get<DayInfo[]>(`/api/holidays/month${qs({ year, month })}`, signal) });
}
export function useWorkingRules() {
  return useQuery({ queryKey: ['working-rules'], queryFn: ({ signal }) => get<WorkingRules>('/api/working-rules', signal) });
}

export function useApprovals(q: { status?: ApprovalStatus; mine?: boolean }, enabled = true) {
  return useQuery({ queryKey: ['approvals', q], queryFn: ({ signal }) => get<Page<ApprovalDTO>>(`/api/approvals${qs({ ...q, limit: 100 })}`, signal), enabled });
}
export function useApprovalCounts(enabled = true) {
  return useQuery({ queryKey: ['approval-counts'], queryFn: ({ signal }) => get<Record<ApprovalStatus, number>>('/api/approvals/counts', signal), enabled });
}
export function useApproval(id: string | undefined) {
  return useQuery({
    queryKey: ['approval', id],
    queryFn: ({ signal }) => get<{ request: ApprovalDTO; actions: ApprovalActionDTO[] }>(`/api/approvals/${id}`, signal),
    enabled: !!id,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: ({ signal }) => get<Page<NotificationDTO> & { unread: number }>('/api/notifications?limit=30', signal),
    refetchInterval: 120_000,
  });
}

export function useAudit(q: Record<string, unknown>, enabled: boolean) {
  return useQuery({ queryKey: ['audit', q], queryFn: ({ signal }) => get<Page<AuditLogDTO>>(`/api/audit${qs(q)}`, signal), enabled, placeholderData: keepPreviousData });
}
export function useHealth(enabled: boolean) {
  return useQuery({ queryKey: ['health'], queryFn: ({ signal }) => get<HealthDTO>('/api/health', signal), enabled, refetchInterval: 30_000 });
}

export function invalidateTasks(qc: QueryClient, id?: string) {
  void qc.invalidateQueries({ queryKey: ['tasks'] });
  void qc.invalidateQueries({ queryKey: keys.dashboard });
  if (id) {
    void qc.invalidateQueries({ queryKey: keys.task(id) });
    void qc.invalidateQueries({ queryKey: keys.history(id) });
  } else {
    void qc.invalidateQueries({ queryKey: ['task'] });
    void qc.invalidateQueries({ queryKey: ['task-history'] });
  }
}

export { useQueryClient };
